import { Component, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { AutomationService } from 'src/app/shared/services/automation.service';

interface LcdFileOption {
  id: string;
  file_name: string;
  payer: string;
  description?: string;
}

@Component({
  selector: 'app-add-lcd-policy-modal',
  templateUrl: './add-lcd-policy-modal.component.html',
  styleUrls: ['./add-lcd-policy-modal.component.scss'],
})
export class AddLcdPolicyModalComponent implements OnInit {
  mode: 'single' | 'range' = 'single';
  cptFrom = '';
  cptTo = '';

  fileSearchTerm = '';
  fileResults: LcdFileOption[] = [];
  selectedFile: LcdFileOption | null = null;
  isSearching = false;
  showResults = false;
  private searchDebounce: any;

  isSubmitting = false;

  constructor(
    public activeModal: NgbActiveModal,
    private automationService: AutomationService,
    private spinnerService: NgxSpinnerService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.searchFiles('');
  }

  setMode(mode: 'single' | 'range'): void {
    this.mode = mode;
    if (mode === 'single') {
      this.cptTo = '';
    }
  }

  onFileSearchInput(): void {
    this.selectedFile = null;
    this.showResults = true;
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => this.searchFiles(this.fileSearchTerm), 300);
  }

  focusFileSearch(): void {
    this.showResults = true;
  }

  private searchFiles(term: string): void {
    this.isSearching = true;
    this.automationService
      .searchLcdFiles(term)
      .then((res) => {
        this.isSearching = false;
        this.fileResults = res?.data || [];
      })
      .catch(() => {
        this.isSearching = false;
        this.fileResults = [];
      });
  }

  selectFile(file: LcdFileOption): void {
    this.selectedFile = file;
    this.fileSearchTerm = file.file_name;
    this.showResults = false;
  }

  clearSelectedFile(): void {
    this.selectedFile = null;
    this.fileSearchTerm = '';
    this.searchFiles('');
  }

  async submit(): Promise<void> {
    if (!this.cptFrom.trim()) {
      this.toastr.error('Please enter a CPT code');
      return;
    }
    if (this.mode === 'range' && !this.cptTo.trim()) {
      this.toastr.error('Please enter the end of the CPT range');
      return;
    }
    if (!this.selectedFile) {
      this.toastr.error('Please select an LCD file');
      return;
    }

    this.isSubmitting = true;
    this.spinnerService.show();
    try {
      const res = await this.automationService.createLcdPolicies(
        this.selectedFile.id,
        this.cptFrom.trim(),
        this.mode === 'range' ? this.cptTo.trim() : undefined
      );
      this.spinnerService.hide();
      this.isSubmitting = false;

      if (res && res.code === 200) {
        const created = res.data?.created ?? 1;
        this.toastr.success(`${created} LCD policy record${created === 1 ? '' : 's'} created successfully!`);
        this.activeModal.close(res);
      } else {
        this.toastr.error(res?.message || 'Failed to create LCD policy');
      }
    } catch (err: any) {
      this.spinnerService.hide();
      this.isSubmitting = false;
      console.error(err);
      this.toastr.error(err?.error?.message || err?.message || 'Failed to create LCD policy');
    }
  }
}
